/**
 * AJ Lead Capture — REST client (manifest, event, convert).
 *
 * Reads config from window.AJ_LO ({ rest, nonce }). Event logging uses
 * sendBeacon when possible so it survives page unload.
 *
 * @module api
 */

const cfg = window.AJ_LO || {};

/**
 * Build request headers, including the REST nonce when present.
 *
 * @returns {Object} Headers.
 */
function headers() {
  const h = { 'Content-Type': 'application/json' };
  if (cfg.nonce) {
    h['X-WP-Nonce'] = cfg.nonce;
  }
  return h;
}

/**
 * Fetch the offer manifest.
 *
 * @returns {Promise<Object|null>} Manifest or null on failure.
 */
export async function getManifest() {
  try {
    const res = await fetch(`${cfg.rest}/manifest`, { credentials: 'omit' });
    if (!res.ok) {
      return null;
    }
    return await res.json();
  } catch (e) {
    return null;
  }
}

/**
 * Log an impression/dismiss event (fire-and-forget).
 *
 * @param {Object} payload { offer_id, event_type, surface, url }.
 * @returns {void}
 */
export function postEvent(payload) {
  const url = `${cfg.rest}/event`;
  const body = JSON.stringify(payload);

  // Prefer sendBeacon (survives unload), but it can't set the nonce header.
  if (!cfg.nonce && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    } catch (e) { /* fall through */ }
  }

  fetch(url, {
    method: 'POST',
    headers: headers(),
    credentials: 'omit',
    keepalive: true,
    body,
  }).catch(() => {});
}

/**
 * Submit a conversion.
 *
 * Resolves only when the server accepted the signup. Anything else rejects
 * with an Error carrying the HTTP `status` (undefined on a network failure)
 * and the server's `message` when it sent one, so a failed submit is never
 * mistaken for a conversion.
 *
 * @param {Object} payload { offer_id, email, name, surface, url }.
 * @returns {Promise<Object>} Server response { ok, message, redirect }.
 */
export async function postConvert(payload) {
  const res = await fetch(`${cfg.rest}/convert`, {
    method: 'POST',
    headers: headers(),
    credentials: 'omit',
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (res.ok && body && body.ok) {
    return body;
  }
  const err = new Error((body && body.message) || `HTTP ${res.status}`);
  err.status = res.status;
  throw err;
}
